import { createPublicApiClient, type PublicApiClient } from '@cloneai/api-client';
import { metrics } from '@cloneai/observability';
import { AVATAR_ANIMATION_PILOT_RENDER_DEADLINE_MS } from '@cloneai/shared-types';
import { useCallback, useEffect, useRef, useState } from 'react';

import { env } from '../env.js';
import type { SessionRecorder } from '../voice/sessionRecorder.js';

const FINAL_STATUS_WATCHDOG_MS = 5_000;
const READY_URL_EXPIRY_SAFETY_MS = 5_000;
const MAX_CONSECUTIVE_POLL_FAILURES = 3;
const HIDDEN_PAGE_RETRY_MS = 1_000;

export type AnimationPlaybackFailure =
  | 'autoplay'
  | 'media-aborted'
  | 'media-error'
  | 'media-expired'
  | 'media-stalled'
  | 'media-timeout';

export interface QueuedAnimationMedia {
  readonly animationId: string;
  readonly playbackUrl: string;
  readonly expiresAt: string;
}

type AnimationPhase = 'idle' | 'polling' | 'media-ready' | 'playing' | 'replay-ready' | 'replaying';

interface AnimationSnapshot {
  readonly phase: AnimationPhase;
  readonly media: QueuedAnimationMedia | null;
}

interface PollRequest {
  readonly animationId: string;
  readonly renderDeadlineAt: string;
  readonly deadlineAtMonotonicMs: number;
  readonly generation: number;
}

export interface QueuedAnimationController {
  readonly phase: AnimationPhase;
  readonly media: QueuedAnimationMedia | null;
  readonly isBusy: boolean;
  readonly start: (animationId: string, renderDeadlineAt: string) => boolean;
  readonly discardReplay: () => void;
  readonly replay: (animationId: string) => boolean;
  readonly onPlaying: (animationId: string) => void;
  readonly onEnded: (animationId: string) => void;
  readonly onMediaFailure: (animationId: string, reason: AnimationPlaybackFailure) => void;
  readonly cancel: () => void;
}

let apiClientSingleton: PublicApiClient | null = null;
function getApiClient(): PublicApiClient {
  if (!apiClientSingleton) {
    apiClientSingleton = createPublicApiClient({ baseUrl: env.VITE_API_BASE_URL });
  }
  return apiClientSingleton;
}

function errorStatus(error: unknown): number | null {
  if (typeof error !== 'object' || error === null || !('status' in error)) return null;
  const status = (error as { status?: unknown }).status;
  return typeof status === 'number' ? status : null;
}

function isTerminalPollError(error: unknown): boolean {
  const status = errorStatus(error);
  return status !== null && status >= 400 && status < 500 && status !== 429;
}

const IDLE_SNAPSHOT: AnimationSnapshot = { phase: 'idle', media: null };

function recordOutcome(outcome: 'cancelled' | 'completed' | 'failed', reason: string): void {
  metrics.increment('agent.lip_animation.outcome', 1, { tags: { outcome, reason } });
}

function recordReplayStarted(): void {
  metrics.increment('agent.lip_animation.replay.started');
}

function recordReplayOutcome(outcome: 'cancelled' | 'completed' | 'failed', reason: string): void {
  metrics.increment('agent.lip_animation.replay.outcome', 1, { tags: { outcome, reason } });
}

/**
 * Owns one queued batch-animation turn. Text remains authoritative: any poll
 * or media failure clears this controller and leaves AgentStage's source video
 * untouched underneath the optional rendered layer.
 */
export function useQueuedAnimation(
  isActive: boolean,
  recorderRef: Readonly<{ current: SessionRecorder | null }>,
  scopeKey: string | null,
): QueuedAnimationController {
  const [request, setRequest] = useState<PollRequest | null>(null);
  const [snapshot, setSnapshot] = useState<AnimationSnapshot>(IDLE_SNAPSHOT);
  const snapshotRef = useRef<AnimationSnapshot>(IDLE_SNAPSHOT);
  const activeAnimationIdRef = useRef<string | null>(null);
  const retainedAnimationIdRef = useRef<string | null>(null);
  const replayAnimationIdRef = useRef<string | null>(null);
  const generationRef = useRef(0);
  const isActiveRef = useRef(isActive);
  const previousScopeKeyRef = useRef(scopeKey);
  isActiveRef.current = isActive;

  const updateSnapshot = useCallback((next: AnimationSnapshot): void => {
    snapshotRef.current = next;
    setSnapshot(next);
  }, []);

  const clearAnimation = useCallback((): void => {
    generationRef.current += 1;
    activeAnimationIdRef.current = null;
    retainedAnimationIdRef.current = null;
    replayAnimationIdRef.current = null;
    setRequest(null);
    updateSnapshot(IDLE_SNAPSHOT);
  }, [updateSnapshot]);

  const cancel = useCallback((): void => {
    if (activeAnimationIdRef.current !== null) {
      recordOutcome('cancelled', 'client-cancelled');
    } else if (replayAnimationIdRef.current !== null) {
      recordReplayOutcome('cancelled', 'client-cancelled');
    }
    clearAnimation();
  }, [clearAnimation]);

  const finishGeneration = useCallback(
    (generation: number, reason: string): void => {
      if (generationRef.current !== generation) return;
      recordOutcome('failed', reason);
      clearAnimation();
    },
    [clearAnimation],
  );

  const start = useCallback(
    (animationId: string, renderDeadlineAt: string): boolean => {
      if (!isActiveRef.current || activeAnimationIdRef.current !== null) return false;
      const parsedDeadlineAtMs = Date.parse(renderDeadlineAt);
      if (!Number.isFinite(parsedDeadlineAtMs)) return false;
      if (replayAnimationIdRef.current !== null) {
        recordReplayOutcome('cancelled', 'superseded');
      }
      const deadlineAtMonotonicMs = performance.now() + AVATAR_ANIMATION_PILOT_RENDER_DEADLINE_MS;
      const generation = generationRef.current + 1;
      generationRef.current = generation;
      activeAnimationIdRef.current = animationId;
      retainedAnimationIdRef.current = null;
      replayAnimationIdRef.current = null;
      updateSnapshot({ phase: 'polling', media: null });
      setRequest({ animationId, renderDeadlineAt, deadlineAtMonotonicMs, generation });
      return true;
    },
    [updateSnapshot],
  );

  const replay = useCallback(
    (animationId: string): boolean => {
      const current = snapshotRef.current;
      if (
        !isActiveRef.current ||
        activeAnimationIdRef.current !== null ||
        replayAnimationIdRef.current !== null ||
        retainedAnimationIdRef.current !== animationId ||
        current.phase !== 'replay-ready' ||
        current.media?.animationId !== animationId
      ) {
        return false;
      }
      const expiresAtMs = Date.parse(current.media.expiresAt);
      if (!Number.isFinite(expiresAtMs) || expiresAtMs <= Date.now() + READY_URL_EXPIRY_SAFETY_MS) {
        retainedAnimationIdRef.current = null;
        updateSnapshot(IDLE_SNAPSHOT);
        return false;
      }

      replayAnimationIdRef.current = animationId;
      updateSnapshot({ phase: 'replaying', media: current.media });
      recordReplayStarted();
      return true;
    },
    [updateSnapshot],
  );

  const discardReplay = useCallback((): void => {
    if (activeAnimationIdRef.current !== null) return;
    if (replayAnimationIdRef.current !== null) {
      recordReplayOutcome('cancelled', 'superseded');
    }
    replayAnimationIdRef.current = null;
    retainedAnimationIdRef.current = null;
    updateSnapshot(IDLE_SNAPSHOT);
  }, [updateSnapshot]);

  const onPlaying = useCallback(
    (animationId: string): void => {
      const current = snapshotRef.current;
      if (current.media?.animationId !== animationId) return;
      if (activeAnimationIdRef.current === animationId) {
        updateSnapshot({ ...current, phase: 'playing' });
      } else if (replayAnimationIdRef.current === animationId) {
        updateSnapshot({ ...current, phase: 'replaying' });
      }
    },
    [updateSnapshot],
  );

  const onEnded = useCallback(
    (animationId: string): void => {
      const current = snapshotRef.current;
      if (current.media?.animationId !== animationId) return;

      if (activeAnimationIdRef.current === animationId) {
        recordOutcome('completed', 'playback-ended');
        generationRef.current += 1;
        activeAnimationIdRef.current = null;
        retainedAnimationIdRef.current = animationId;
        setRequest(null);
      } else if (replayAnimationIdRef.current === animationId) {
        recordReplayOutcome('completed', 'playback-ended');
        replayAnimationIdRef.current = null;
      } else {
        return;
      }

      const expiresAtMs = Date.parse(current.media.expiresAt);
      if (!Number.isFinite(expiresAtMs) || expiresAtMs <= Date.now() + READY_URL_EXPIRY_SAFETY_MS) {
        retainedAnimationIdRef.current = null;
        updateSnapshot(IDLE_SNAPSHOT);
        return;
      }
      updateSnapshot({ phase: 'replay-ready', media: current.media });
    },
    [updateSnapshot],
  );

  const onMediaFailure = useCallback(
    (animationId: string, reason: AnimationPlaybackFailure): void => {
      if (activeAnimationIdRef.current === animationId) {
        recordOutcome('failed', reason);
      } else if (replayAnimationIdRef.current === animationId) {
        recordReplayOutcome('failed', reason);
      } else {
        return;
      }
      clearAnimation();
    },
    [clearAnimation],
  );

  useEffect(() => {
    if (previousScopeKeyRef.current === scopeKey) return;
    previousScopeKeyRef.current = scopeKey;
    cancel();
  }, [cancel, scopeKey]);

  useEffect(() => {
    if (!isActive) cancel();
  }, [cancel, isActive]);

  useEffect(() => {
    if (snapshot.phase !== 'replay-ready' || !snapshot.media) return undefined;
    const animationId = snapshot.media.animationId;
    const expiresAtMs = Date.parse(snapshot.media.expiresAt);
    const delayMs = expiresAtMs - Date.now() - READY_URL_EXPIRY_SAFETY_MS;
    const expire = (): void => {
      if (
        replayAnimationIdRef.current === null &&
        retainedAnimationIdRef.current === animationId &&
        snapshotRef.current.phase === 'replay-ready'
      ) {
        retainedAnimationIdRef.current = null;
        updateSnapshot(IDLE_SNAPSHOT);
      }
    };
    if (!Number.isFinite(delayMs) || delayMs <= 0) {
      expire();
      return undefined;
    }
    const timer = setTimeout(expire, delayMs);
    return () => clearTimeout(timer);
  }, [snapshot, updateSnapshot]);

  useEffect(() => {
    if (!request || !isActive) return undefined;

    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let wakeWait: (() => void) | null = null;
    const controller = new AbortController();
    const deadlineAt = request.deadlineAtMonotonicMs;
    const remainingPollMs = (): number => deadlineAt - performance.now();

    const isCurrent = (): boolean =>
      !cancelled &&
      isActiveRef.current &&
      generationRef.current === request.generation &&
      activeAnimationIdRef.current === request.animationId;

    const wait = (delayMs: number): Promise<void> =>
      new Promise((resolve) => {
        wakeWait = (): void => {
          if (timer) clearTimeout(timer);
          timer = null;
          wakeWait = null;
          resolve();
        };
        timer = setTimeout(wakeWait, delayMs);
      });

    const deadlineTimer = setTimeout(
      () => {
        controller.abort(new DOMException('Animation polling deadline exceeded.', 'AbortError'));
        if (isCurrent()) finishGeneration(request.generation, 'poll-timeout');
      },
      Math.max(0, remainingPollMs() + FINAL_STATUS_WATCHDOG_MS),
    );

    const poll = async (): Promise<void> => {
      const recorder = recorderRef.current;
      const credentials = await recorder?.getSessionCredentials();
      if (!isCurrent()) return;
      if (!credentials) {
        finishGeneration(request.generation, 'session-credentials-unavailable');
        return;
      }

      let consecutiveFailures = 0;
      let finalPollStarted = false;
      for (;;) {
        if (!isCurrent()) return;
        const remainingMs = remainingPollMs();
        if (remainingMs <= 0 && finalPollStarted) {
          finishGeneration(request.generation, 'poll-timeout');
          return;
        }
        if (document.visibilityState === 'hidden' && remainingMs > 0) {
          await wait(Math.min(HIDDEN_PAGE_RETRY_MS, remainingMs));
          continue;
        }
        if (remainingMs <= 0) finalPollStarted = true;

        try {
          const status = await getApiClient().publicSessions.getAnimation(
            credentials.sessionId,
            request.animationId,
            { sessionToken: credentials.sessionToken, signal: controller.signal },
          );
          if (!isCurrent()) return;
          if (status.animationId !== request.animationId) {
            finishGeneration(request.generation, 'status-id-mismatch');
            return;
          }

          consecutiveFailures = 0;
          if (status.kind === 'failed') {
            finishGeneration(request.generation, 'renderer-failed');
            return;
          }
          if (status.kind === 'ready') {
            const expiresAtMs = Date.parse(status.expiresAt);
            if (
              !Number.isFinite(expiresAtMs) ||
              expiresAtMs <= Date.now() + READY_URL_EXPIRY_SAFETY_MS
            ) {
              finishGeneration(request.generation, 'playback-url-expired');
              return;
            }
            clearTimeout(deadlineTimer);
            updateSnapshot({
              phase: 'media-ready',
              media: {
                animationId: status.animationId,
                playbackUrl: status.playbackUrl,
                expiresAt: status.expiresAt,
              },
            });
            return;
          }

          if (status.renderDeadlineAt !== request.renderDeadlineAt) {
            finishGeneration(request.generation, 'status-deadline-mismatch');
            return;
          }
          if (finalPollStarted) {
            finishGeneration(request.generation, 'poll-timeout');
            return;
          }

          const remainingAfterResponseMs = remainingPollMs();
          if (remainingAfterResponseMs <= 0) continue;

          await wait(Math.min(status.retryAfterMs, remainingAfterResponseMs));
        } catch (error) {
          if (!isCurrent()) return;
          if (controller.signal.aborted) return;
          consecutiveFailures += 1;
          if (isTerminalPollError(error) || consecutiveFailures >= MAX_CONSECUTIVE_POLL_FAILURES) {
            finishGeneration(
              request.generation,
              isTerminalPollError(error) ? 'poll-client-error' : 'poll-retry-exhausted',
            );
            return;
          }
          if (finalPollStarted) {
            finishGeneration(request.generation, 'poll-timeout');
            return;
          }
          const remainingAfterFailureMs = remainingPollMs();
          if (remainingAfterFailureMs <= 0) continue;
          await wait(
            Math.min(5_000, 500 * 2 ** (consecutiveFailures - 1), remainingAfterFailureMs),
          );
        }
      }
    };

    void poll();
    return () => {
      cancelled = true;
      controller.abort(new DOMException('Animation polling cancelled.', 'AbortError'));
      clearTimeout(deadlineTimer);
      if (timer) clearTimeout(timer);
      wakeWait?.();
    };
  }, [finishGeneration, isActive, recorderRef, request, updateSnapshot]);

  return {
    phase: snapshot.phase,
    media: snapshot.media,
    isBusy: snapshot.phase !== 'idle' && snapshot.phase !== 'replay-ready',
    start,
    discardReplay,
    replay,
    onPlaying,
    onEnded,
    onMediaFailure,
    cancel,
  };
}
